import { Labeled } from '@components/form/Labeled'
import type { SettlementDetail } from '@services/settlements/types'
import type { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Textarea } from '@/components/ui/textarea'

import { SettlementBlockTable } from '../SettlementBlockTable'
import { SettlementCheckBadge } from '../SettlementCheckBadge'
import { SettlementBalanceRow } from '../SettlementBalanceRow'
import { StepIntro } from './fields'
import type { SettlementFormValues } from './useSettlementInputsForm'

const GROUPS = ['services', 'electricity', 'other'] as const

export function StepSummary({
  settlement,
  form,
  readOnly
}: {
  settlement: SettlementDetail
  form: UseFormReturn<SettlementFormValues>
  readOnly: boolean
}) {
  const { t } = useTranslation('settlement')
  const result = settlement.result

  const blocks = result.blocks.filter((block) => block.lines.length > 0)
  const groups = GROUPS.filter(
    (key) =>
      Number(result[key].cost) !== 0 || Number(result[key].advances) !== 0
  )

  return (
    <div className="space-y-5">
      <StepIntro>{t('wizard.summary.intro')}</StepIntro>

      {settlement.checks.length > 0 && (
        <div className="space-y-2">
          {settlement.checks.map((check, index) => (
            <SettlementCheckBadge key={`${check.code}-${index}`} check={check} />
          ))}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {blocks.map((block) => (
          <SettlementBlockTable key={block.code} block={block} />
        ))}
      </div>

      <div className="rounded-2xl border border-ink/[0.08] p-4">
        {groups.map((key) => (
          <SettlementBalanceRow
            key={key}
            label={t(`result.groups.${key}`)}
            balance={result[key]}
          />
        ))}
      </div>

      {!readOnly && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled
            label={t('fields.tenant_note')}
            hint={t('hints.tenant_note')}
          >
            <Textarea rows={3} {...form.register('tenant_note')} />
          </Labeled>
          <Labeled
            label={t('fields.internal_note')}
            hint={t('hints.internal_note')}
          >
            <Textarea rows={3} {...form.register('internal_note')} />
          </Labeled>
        </div>
      )}
    </div>
  )
}
